import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Search, Instagram, Facebook, Globe, Phone } from 'lucide-react';
import CategoryNav from './CategoryNav';
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from './NewsTicker';
import SocialLinks, { SOCIAL_FIELDS, countSocialLinks } from './SocialLinks';

// The platform datatable declares this multiselect as a `string` column, so a
// saved array comes back comma-joined ("Dance Schools,Choreographers") rather
// than as an array. Split it back apart — an `Array.isArray` check fails on a
// string, and a plain `===` compare can never match a multi-value pick.
function toTypeArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  if (typeof v === "string") return v.split(",").map((x) => x.trim()).filter(Boolean);
  return [];
}


const SHOPPING_FIELDS: FieldDef[] = [
  { key: "name", label: "Store name", required: true },
  { key: "categories", label: "Type", type: "multiselect", options: ['Clothing', 'Jewelry', 'Accessories', 'Global Brands'], help: "Controls which \"Explore by Type\" filter(s) this store shows under. Select one or more." },
  { key: "address", label: "Address", type: "textarea" },
  { key: "location", label: "Location", placeholder: "Frisco" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "shopUrl", label: "Shop / social URL", type: "url", placeholder: "https://…" },
  { key: "phone", label: "Phone (Call button)", placeholder: "214-662-4936" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

const LOCATION_FILTERS = ['Addison', 'Allen', 'Anna', 'Arlington', 'Aubrey', 'Carrollton', 'Cedar Hill', 'Coppell', 'Dallas', 'Denton', 'DeSoto', 'Duncanville', 'Farmers Branch', 'Flower Mound', 'Fort Worth', 'Frisco', 'Garland', 'Grand Prairie', 'Hutchins', 'Irving', 'Lewisville', 'Mansfield', 'McKinney', 'Mesquite', 'Midlothian', 'Plano', 'Richardson', 'Rockwall', 'Rowlett', 'Terrell', 'Other'];
const HERO_LOCATIONS = ['All Locations', 'Addison', 'Allen', 'Anna', 'Arlington', 'Aubrey', 'Carrollton', 'Cedar Hill', 'Coppell', 'Dallas', 'Denton', 'DeSoto', 'Duncanville', 'Farmers Branch', 'Flower Mound', 'Fort Worth', 'Frisco', 'Garland', 'Grand Prairie', 'Hutchins', 'Irving', 'Lewisville', 'Mansfield', 'McKinney', 'Mesquite', 'Midlothian', 'Plano', 'Prosper', 'Richardson', 'Rockwall', 'Rowlett', 'Southlake', 'Terrell', 'The Colony', 'Other'];

const BRAND_IMAGE = 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-12/e5ebd40a6ede.jpeg';
const FASHION_IMAGE = 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-12/3367b709ba9c.jpeg';

const SHOPPING_CATEGORIES = ['Clothing', 'Jewelry', 'Accessories', 'Global Brands'] as const;

export const SHOPPING_STORES = [
  {
    id: 'joyalukkas-frisco',
    name: 'Joyalukkas Frisco',
    address: '7055 Preston Rd, Frisco, TX 75034',
    location: 'Frisco',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784415236774-lppyso.png',
    instagram: 'https://www.instagram.com/reel/DPt1-BND2J2/',
    description: 'Jewelry store',
    categories: ['Jewelry'],
  },
  {
    id: 'pujas-trendz',
    name: 'PujasTrendz',
    address: '10202 Coit Rd #400, Frisco, TX 75035',
    location: 'Frisco',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783461651404-wmu5jl.png',
    shopUrl: 'https://www.instagram.com/pujastrendz/',
    instagram: 'https://www.instagram.com/p/DefjXTLs3Nd/',
    description: '',
    categories: ['Clothing', 'Jewelry'],
  },
  {
    id: 'sri-krishna-jewellers',
    name: 'Sri Krishna Jewellers',
    address: '13145 Main St Ste 220, Frisco, TX 75035',
    location: 'Frisco',
    image: BRAND_IMAGE,
    shopUrl: '',
    description: '',
    categories: ['Jewelry', 'Global Brands'],
  },
  {
    id: 'malabar-gold',
    name: 'Malabar Gold and Diamonds',
    address: '5811 Preston Rd, Frisco, TX 75034',
    location: 'Frisco',
    image: BRAND_IMAGE,
    shopUrl: '',
    description: '',
    categories: ['Jewelry', 'Global Brands'],
  },
  {
    id: 'tanmai-jewelers',
    name: 'Tanmai Jewelers',
    address: '608 S Valley Ranch Pkwy #108, Irving, TX 75063',
    location: 'Irving',
    image: BRAND_IMAGE,
    shopUrl: '',
    description: '',
    categories: ['Jewelry', 'Global Brands'],
  },
  {
    id: 'govindjis-jewelers',
    name: "Govindji's Jewelers",
    address: '4646 Dubai Way, Frisco, TX 75034',
    location: 'Frisco',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784561983168-ubj0xe.png',
    shopUrl: '',
    description: '',
    category: 'Global Brands',
  },
  {
    id: 'manyavar',
    name: 'Manyavar',
    address: '3285 Regent Blvd Ste 100, Irving, TX 75063',
    location: 'Irving',
    image: BRAND_IMAGE,
    shopUrl: '',
    description: '',
    categories: ['Clothing', 'Global Brands'],
  },
  {
    id: 'mahimas-collection',
    name: 'Mahimas Collection',
    address: '@mahimascollectionllc',
    location: 'Online',
    image: FASHION_IMAGE,
    shopUrl: 'https://www.instagram.com/mahimascollectionllc/',
    description: 'Instagram boutique',
    category: 'Clothing',
  },
  {
    id: 'neelamstrunk',
    name: 'Neelams Trunk',
    address: '@neelamstrunk',
    location: 'Online',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783515207003-bqiy0j.png',
    shopUrl: 'https://www.instagram.com/neelamstrunk/',
    description: 'Instagram boutique',
    category: 'Clothing',
  },
  {
    id: 'shoplabeln',
    name: 'Shop Label N',
    address: '@shoplabeln',
    location: 'Online',
    image: FASHION_IMAGE,
    shopUrl: 'https://www.instagram.com/shoplabeln/',
    description: 'Instagram boutique',
    category: 'Clothing',
  },
  {
    id: 'saree-strong',
    name: 'Saree Strong',
    address: 'McDermott Rd, Suite 200, Plano, TX 75025',
    location: 'Plano',
    image: FASHION_IMAGE,
    shopUrl: '',
    description: 'Clothing store',
    category: 'Clothing',
  },
  {
    id: 'destinationsaree',
    name: 'Destination Saree',
    address: '2050 Spring Creek Pkwy suite 203, Plano, TX 75023',
    location: 'Plano',
    image: FASHION_IMAGE,
    shopUrl: '',
    description: 'Clothing & Jewelry store',
    categories: ['Clothing', 'Jewelry'],
  },
  {
    id: 'shona-sarees',
    name: 'Shona Sarees',
    subtitle: 'Beautiful and Elegant Sarees',
    address: '',
    location: 'Plano',
    image: FASHION_IMAGE,
    shopUrl: 'https://www.instagram.com/shonasaree/',
    description: 'Saree boutique',
    categories: ['Clothing'],
  },
  {
    id: 'elegance-by-monika',
    name: 'Elegance by Monika',
    address: '1400 N Coit Rd Ste 903, McKinney, TX 75071',
    location: 'McKinney',
    image: FASHION_IMAGE,
    shopUrl: '',
    description: 'Clothing & Jewelry boutique',
    categories: ['Clothing', 'Jewelry'],
  },
  {
    id: 'hashtag-jewelers',
    name: 'Hashtag Jewelers',
    address: '975 Sam Rayburn Tollway, Suite 180, Allen, TX 75013',
    location: 'Allen',
    image: BRAND_IMAGE,
    shopUrl: '',
    description: 'Jewelry store',
    categories: ['Jewelry'],
  },
  {
    id: 'marc-samuels-jewelers',
    name: 'Marc Samuels Jewelers',
    address: '3000 Grapevine Mills Pkwy #515A, Grapevine, TX 76051',
    location: 'Grapevine',
    image: BRAND_IMAGE,
    shopUrl: '',
    description: 'Jewelry store',
    category: 'Jewelry',
  },
    {
    id: 'lushkara-designs',
    name: 'Lushkara Designs',
    address: '@lushkaradesigns',
    location: 'Online',
    image: FASHION_IMAGE,
    shopUrl: 'https://www.instagram.com/lushkaradesigns/',
    description: 'Clothing & Jewelry boutique',
    categories: ['Clothing', 'Jewelry'],
  },
  {
    id: 'silk-threads',
    name: 'Silk Threads',
    address: '2540 N Josey Ln #100, Carrollton, TX 75006',
    location: 'Carrollton, TX',
    image: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-08/07b0a7a28b87.jpeg',
    instagram: 'https://www.instagram.com/p/C4nvArdLpIz/',
    description: 'Clothing & Jewelry boutique',
    categories: ['Clothing', 'Jewelry'],
  },
  {
    id: 'jewelbox-by-rubani',
    name: 'JewelBox By Rubani',
    address: '@jewelboxbyrubani',
    location: 'Online',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783744243962-ndtg0s.png',
    shopUrl: 'https://www.instagram.com/jewelboxbyrubani/',
    description: 'Jewelry boutique',
    category: 'Jewelry',
  },
  {
    id: 'virupa-couture',
    name: 'Virupa Couture',
    address: '@virupacouture',
    location: 'Online',
    image: BRAND_IMAGE,
    shopUrl: 'https://www.instagram.com/virupacouture/',
    description: 'Couture boutique',
    category: 'Clothing',
  },
  {
    id: 'pret-by-allu',
    name: 'Pret By Allu',
    subtitle: 'Pakistani Couture',
    address: '@pret_by_allu',
    location: 'Online',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783461867589-rca5cm.png',
    shopUrl: 'https://www.instagram.com/pret_by_allu/',
    description: 'Clothing boutique, Pakistani Couture',
    category: 'Clothing',
  },
  {
    id: 'vajra',
    name: 'Vajra',
    address: '@jewellery_vajra',
    location: 'Online',
    image: BRAND_IMAGE,
    shopUrl: 'https://www.instagram.com/jewellery_vajra/',
    description: 'Jewelry boutique',
    category: 'Jewelry',
  },
  {
    id: 'zariya-trends',
    name: 'Zariya Trends',
    subtitle: 'Clutches, Potlis, Dupattas, Shawls & Capes',
    address: '',
    location: 'Online',
    image: BRAND_IMAGE,
    shopUrl: 'https://www.instagram.com/zariya.trends/',
    description: 'Accessories boutique',
    category: 'Accessories',
  },
  {
    id: 'mandiras-boutique',
    name: 'Mandira’s Boutique',
    address: '',
    location: 'Online',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784427988047-ab1emh.png',
    shopUrl: 'https://www.instagram.com/mandirasboutique',
    description: 'Clothing, Jewelry & Accessories boutique',
    categories: ['Clothing', 'Jewelry', 'Accessories'],
  },
  {
    id: 'jewelry-by-priyana',
    name: 'Jewelry By Priyana',
    address: '@jewelrybypriyana',
    location: 'Online',
    image: BRAND_IMAGE,
    shopUrl: 'https://www.instagram.com/jewelrybypriyana/',
    description: 'Jewelry boutique',
    category: 'Jewelry',
  },
  {
    id: 'designz-by-raga',
    name: 'Designz By Raga',
    subtitle: 'Luxury Resort Wear',
    location: 'Online',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783694950394-xpi3n9.png',
    shopUrl: 'https://www.instagram.com/designzbyraga/',
    instagram: 'https://www.instagram.com/tastebudsbyanubhi/reel/C7b5CWQsAhV/',
    description: 'Clothing & Jewelry boutique, Luxury Resort Wear',
    categories: ['Clothing', 'Jewelry'],
  },
  {
    id: 'rivaaz-by-radhika',
    name: 'Rivaaz by Radhika',
    address: 'The Colony',
    location: 'Other',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783470121213-iafwr6.png',
    shopUrl: 'https://www.instagram.com/rivaazbyradhika?igsh=MWZlanplNnBsdmxpcQ%3D%3D',
    description: 'Clothing boutique',
    category: 'Clothing',
  },
  {
    id: 'tassels-by-renu',
    name: 'Tassels By Renu',
    address: '',
    location: 'Frisco',
    image: 'https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-09/34ceddc8b535.jpeg',
    shopUrl: 'https://www.instagram.com/tasselsbyrenu?igsh=MXB0N3U2OWJiYTM3OA%3D%3D&utm_source=qr',
    description: 'Clothing & Jewelry boutique',
    categories: ['Clothing', 'Jewelry'],
  },
  {
    id: 'amir-adnan',
    name: 'Amir Adnan',
    subtitle: "Pioneering Men's Pakistani Couture",
    address: '3031 Preston Rd Ste 600, Frisco, TX 75034',
    location: 'Frisco',
    image: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-12/5d59bb34ca94.jpeg',
    shopUrl: '',
    secondaryShopUrl: 'https://www.instagram.com/amiradnanofficial/',
    description: 'Pakistani Couture',
    category: 'Clothing',
  },
  {
    id: 'pc-the-label',
    name: 'PC The Label',
    subtitle: 'Luxury brand of contemporary & modern outfit!',
    address: '',
    location: 'Online',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783743969522-a9lhk8.png',
    shopUrl: 'https://www.instagram.com/pc_thelabel_/',
    description: 'Luxury brand of contemporary & modern outfit!',
    category: 'Clothing',
  },
  {
    id: 'kimayas-hub',
    name: "Kimaya's Hub",
    address: '3417 Keechi Creek Drive, Prosper, TX 75078',
    location: 'Prosper',
    image: 'https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-10/b41cbeeb4270.jpeg',
    shopUrl: '',
    instagram: 'https://www.instagram.com/kimaya_hub',
    description: 'Shopping boutique',
  },
  {
    id: 'house-of-meraki',
    name: 'House of Meraki',
    subtitle: 'Ethereal Indian & Fusion Jewelry',
    address: '',
    location: 'Online',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784307585138-hdnba.jpg',
    shopUrl: 'https://www.instagram.com/houseofmeraki_indianjewelry/',
    description: 'Ethereal Indian & Fusion Jewelry',
    category: 'Jewelry',
  },
  {
    id: 'shipra-creations',
    name: 'Shipra Creations',
    address: '',
    location: 'Online',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784307585120-a90tfj.jpg',
    shopUrl: 'https://www.instagram.com/shipracreations/',
    description: 'Jewelry boutique',
    category: 'Jewelry',
  },
  {
    id: 'shri-tirumala',
    name: 'Shri Tirumala',
    address: 'Dallas, TX',
    location: 'Dallas',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784380293419-cv5j6o.png',
    shopUrl: 'https://www.facebook.com/share/17NX67rt1o/',
    facebook: 'https://www.facebook.com/share/17NX67rt1o/',
    description: 'Clothing boutique',
    categories: ['Clothing', 'Jewelry'],
  },
  {
    id: 'ulka-talati-jewellery',
    name: 'Ulka Talati Jewellery',
    address: '',
    location: 'Online',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784445694496-sixtl.png',
    shopUrl: 'https://www.facebook.com/ulkatalatijewellery/',
    facebook: 'https://www.facebook.com/ulkatalatijewellery/',
    description: 'Jewelry boutique',
    category: 'Jewelry',
  },
  {
    id: 'lovange-llc',
    name: 'Lovange LLC',
    address: '',
    location: 'Online',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1785509462552-pkos1r.png',
    instagram: 'https://www.instagram.com/lovangedesignstudio/',
    phone: '214-662-4936',
    description: 'Bridal wear and Exquisite special occasion Couture',
    categories: ['Clothing'],
  },
  {
    id: 'mannu-wedding-accessories',
    name: 'Mannu Wedding Accessories',
    address: '',
    location: 'Online',
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784709260922-tsq3wf.png',
    shopUrl: 'https://www.instagram.com/manuu_wedding_accessories',
    instagram: 'https://www.instagram.com/manuu_wedding_accessories',
    description: 'Telugu Wedding Accessories, Wedding Garlands, Loose Flowers',
    category: 'Accessories',
    categories: ['Accessories'],
  },
];

function mapsUrl(name: string, address: string) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name}, ${address}`)}`;
}

// Every shopping store's public detail-page URL is derived from its stable id,
// or (for admin-added cards with no preset id) its name — same pattern as
// grocerySlug in Grocery.tsx. Used by both the card link and
// ShoppingDetailPage's lookup.
export function shoppingSlug(s: { id?: string; name?: string }) {
  return slugify(s.id || s.name || '');
}

// Typo guard: the dashboard description for House of Mykaa contains
// "hamdpicked". Correct it at render time (listing card + detail page) until
// the dashboard row itself is edited. Harmless for every other store.
export function fixStoreDescription(d: unknown): string {
  return String(d || '').replace(/hamdpicked/gi, 'handpicked');
}

export const FEATURED_STORE_NAMES = new Set(['Joyalukkas Frisco', 'Destination Saree', 'Shona Sarees', 'Zariya Trends', 'Malabar Gold and Diamonds', 'Lushkara Designs', 'Saree Strong', 'Designz By Raga', 'Silk Threads', 'PujasTrendz']);

// Rule: a card with a real street address is a physical location — always show
// "View Location" (linking to Maps), never "Shop Now", even if a shopUrl/social
// link is also configured on the card.
const FORCE_VIEW_LOCATION_NAMES = new Set(['Lushkara Designs']);

function isRealAddress(address?: string) {
  const a = (address || '').trim();
  if (!a || a.startsWith('@')) return false;
  return /\d/.test(a);
}

export function ShoppingCard({ store, index, compact, tightFeatured, forceFeatured, linkDetail, detailBase = '/shopping', linkImageDetail }: { store: { name: string; subtitle?: string; address?: string; description?: string; image: string; shopUrl?: string; secondaryShopUrl?: string; secondaryLinkLabel?: string; linkLabel?: string }; index: number; compact?: boolean; tightFeatured?: boolean; forceFeatured?: boolean; linkDetail?: boolean; detailBase?: string; linkImageDetail?: boolean }) {
  const hasAddress = isRealAddress(store.address) || FORCE_VIEW_LOCATION_NAMES.has(store.name);
  const hasShopUrl = !!store.shopUrl && !hasAddress;
  const isFacebook = hasShopUrl && store.shopUrl!.includes('facebook.com');
  const isWebsiteLink = hasShopUrl && !isFacebook && !!store.linkLabel;
  const href = hasShopUrl ? store.shopUrl! : mapsUrl(store.name, store.address || '');
  const linkLabel = hasShopUrl ? (isFacebook ? 'Facebook' : store.linkLabel || 'Shop Now') : 'View Location';
  const isFeatured = forceFeatured || FEATURED_STORE_NAMES.has(store.name);
  // Only show the Shop Now / View Location button when there's an actual shop
  // URL or a real street address — a card with neither (e.g. "Multiple
  // locations in DFW") has nowhere meaningful to send the map link.
  const showPrimaryButton = hasShopUrl || hasAddress;
  // Round icon badges are reserved for "Featured by TastebudsByAnubhi" cards.
  // Everywhere else, a configured website/instagram link renders as a full,
  // labeled button instead of a bare icon.
  const storeAny = store as any;
  const nonFeaturedWebsite = !isFeatured ? (storeAny.website || storeAny.websiteUrl || storeAny.website_url || '') : '';
  const nonFeaturedInstagram = !isFeatured ? (storeAny.instagram || storeAny.instagramUrl || storeAny.instagram_url || '') : '';
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className={`bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col group ${compact ? 'rounded-xl' : ''}`}
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      {linkDetail && linkImageDetail ? (
        <Link
          to={`${detailBase}/${shoppingSlug(store as any)}`}
          aria-label={`View ${store.name} details`}
          className={`relative w-full overflow-hidden bg-gray-50 block ${compact ? 'h-24' : 'h-36'}`}
        >
          <img
            src={store.image}
            alt={store.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=400&fit=crop';
            }}
          />
        </Link>
      ) : (
        <div className={`relative w-full overflow-hidden bg-gray-50 ${compact ? 'h-24' : 'h-36'}`}>
          <img
            src={store.image}
            alt={store.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=400&fit=crop';
            }}
          />
        </div>
      )}
      <div className={`flex flex-col flex-1 ${compact ? 'px-2.5 py-2 gap-1' : 'px-3 py-3 gap-1.5'}`}>
        <h3 className={`font-semibold text-gray-900 leading-snug ${compact ? 'text-[12px]' : 'text-[14px]'}`}>
          {linkDetail ? (
            <Link to={`${detailBase}/${shoppingSlug(store as any)}`} className="hover:text-[#f97316] hover:underline transition-colors">
              {store.name}
            </Link>
          ) : (
            store.name
          )}
        </h3>
        {store.subtitle && (
          <p className={`font-medium text-blue-600 leading-snug ${compact ? 'text-[10px]' : 'text-[12px]'}`}>{store.subtitle}</p>
        )}
        {store.description && (
          <p className={`text-gray-500 leading-snug ${compact ? 'text-[10px]' : 'text-[12px]'}`}>{fixStoreDescription(store.description)}</p>
        )}
        {store.address && (
          <div className="flex items-start gap-1">
            <MapPin className={`text-gray-400 shrink-0 mt-0.5 ${compact ? 'w-[11px] h-[11px]' : 'w-[13px] h-[13px]'}`} />
            <p className={`text-gray-500 leading-relaxed ${compact ? 'text-[10px]' : 'text-[12px]'}`}>{store.address}</p>
          </div>
        )}
        {/* Featured badge — for TastebudsByAnubhi featured stores */}
        {isFeatured && tightFeatured ? (
          <div className="mt-auto pt-1 flex flex-col gap-1.5">
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={
                hasShopUrl
                  ? isFacebook
                    ? 'inline-flex items-center gap-1 font-semibold text-white bg-[#1877F2] rounded-full hover:bg-[#166fe5] transition-all duration-200 shadow-sm px-2.5 py-1 text-[10px] whitespace-nowrap self-start'
                    : 'inline-flex items-center gap-1 font-semibold text-white bg-gradient-to-r from-[#f97316] to-[#ea580c] rounded-full hover:from-[#ea580c] hover:to-[#dc4d00] transition-all duration-200 shadow-sm px-2.5 py-1 text-[10px] whitespace-nowrap self-start'
                  : 'inline-flex items-center gap-1 font-semibold text-[#f97316] border border-[#f97316] rounded-full hover:bg-[#f97316] hover:text-white transition-all duration-200 px-2.5 py-1 text-[10px] whitespace-nowrap self-start'
              }
            >
              {hasShopUrl ? (isFacebook ? <Facebook className="w-[11px] h-[11px]" /> : isWebsiteLink ? <Globe className="w-[11px] h-[11px]" /> : <Instagram className="w-[11px] h-[11px]" />) : <MapPin className="w-[11px] h-[11px]" />}
              {linkLabel}
            </a>
            <div className="flex items-center gap-1.5">
              <p className="text-[10px] font-semibold text-green-600 tracking-wide leading-tight">
                Featured by TastebudsByAnubhi
              </p>
              <SocialLinks item={store} px={20} />
              {!countSocialLinks(store) && hasShopUrl && (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={isFacebook ? 'Facebook' : 'Shop Now'}
                  className={
                    isFacebook
                      ? 'inline-flex items-center justify-center shrink-0 w-6 h-6 rounded-full text-white bg-[#1877F2] hover:bg-[#166fe5] transition-all duration-200 shadow-sm'
                      : 'inline-flex items-center justify-center shrink-0 w-6 h-6 rounded-full text-white bg-gradient-to-r from-[#f97316] to-[#ea580c] hover:from-[#ea580c] hover:to-[#dc4d00] transition-all duration-200 shadow-sm'
                  }
                >
                  {isFacebook ? <Facebook className="w-3 h-3" /> : <Instagram className="w-3 h-3" />}
                </a>
              )}
            </div>
          </div>
        ) : (
          <>
            {isFeatured && (
              <div className="pt-1 pb-1 flex items-center gap-1.5">
                <p className="text-[10px] font-semibold text-green-600 tracking-wide whitespace-nowrap">
                  Featured by TastebudsByAnubhi
                </p>
                <SocialLinks item={store} px={20} />
              </div>
            )}
            <div className={`${isFeatured ? 'mt-auto' : ''} ${compact ? 'pt-1' : 'pt-1.5'} flex flex-wrap items-center gap-1.5`}>
              {isFeatured ? null : (
                <>
                  {nonFeaturedWebsite && (
                    <a
                      href={nonFeaturedWebsite}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className={`inline-flex items-center gap-1 font-semibold text-white bg-blue-600 rounded-full hover:bg-blue-700 transition-all duration-200 shadow-sm ${compact ? 'px-3 py-1 text-[11px]' : 'px-4 py-1.5 text-[13px]'}`}
                    >
                      <Globe className={`${compact ? 'w-[11px] h-[11px]' : 'w-[13px] h-[13px]'}`} />
                      Website
                    </a>
                  )}
                  {nonFeaturedInstagram && (
                    <a
                      href={nonFeaturedInstagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className={`inline-flex items-center gap-1 font-semibold text-white bg-gradient-to-br from-pink-500 to-orange-400 rounded-full hover:from-pink-600 hover:to-orange-500 transition-all duration-200 shadow-sm ${compact ? 'px-3 py-1 text-[11px]' : 'px-4 py-1.5 text-[13px]'}`}
                    >
                      <Instagram className={`${compact ? 'w-[11px] h-[11px]' : 'w-[13px] h-[13px]'}`} />
                      Instagram
                    </a>
                  )}
                </>
              )}
              {showPrimaryButton && (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={
                    hasShopUrl
                      ? isFacebook
                        ? `inline-flex items-center gap-1 font-semibold text-white bg-[#1877F2] rounded-full hover:bg-[#166fe5] transition-all duration-200 shadow-sm ${compact ? 'px-3 py-1 text-[11px]' : 'px-4 py-1.5 text-[13px]'}`
                        : isWebsiteLink
                          ? `inline-flex items-center gap-1 font-semibold text-white bg-blue-600 rounded-full hover:bg-blue-700 transition-all duration-200 shadow-sm ${compact ? 'px-3 py-1 text-[11px]' : 'px-4 py-1.5 text-[13px]'}`
                          : `inline-flex items-center gap-1 font-semibold text-white bg-gradient-to-r from-[#f97316] to-[#ea580c] rounded-full hover:from-[#ea580c] hover:to-[#dc4d00] transition-all duration-200 shadow-sm ${compact ? 'px-3 py-1 text-[11px]' : 'px-4 py-1.5 text-[13px]'}`
                      : `inline-flex items-center gap-1 font-semibold text-[#f97316] border border-[#f97316] rounded-full hover:bg-[#f97316] hover:text-white transition-all duration-200 ${compact ? 'px-3 py-1 text-[11px]' : 'px-4 py-1.5 text-[13px]'}`
                  }
                >
                  {hasShopUrl ? (isFacebook ? <Facebook className={`${compact ? 'w-[11px] h-[11px]' : 'w-[13px] h-[13px]'}`} /> : isWebsiteLink ? <Globe className={`${compact ? 'w-[11px] h-[11px]' : 'w-[13px] h-[13px]'}`} /> : <Instagram className={`${compact ? 'w-[11px] h-[11px]' : 'w-[13px] h-[13px]'}`} />) : <MapPin className={`${compact ? 'w-[11px] h-[11px]' : 'w-[13px] h-[13px]'}`} />}
                  {linkLabel}
                </a>
              )}
              {store.secondaryShopUrl && (
                <a
                  href={store.secondaryShopUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={
                    store.secondaryLinkLabel === 'Instagram'
                      ? `inline-flex items-center gap-1 font-semibold text-white bg-gradient-to-br from-pink-500 to-orange-400 rounded-full hover:from-pink-600 hover:to-orange-500 transition-all duration-200 shadow-sm ${compact ? 'px-3 py-1 text-[11px]' : 'px-4 py-1.5 text-[13px]'}`
                      : `inline-flex items-center gap-1 font-semibold text-white bg-gradient-to-r from-[#f97316] to-[#ea580c] rounded-full hover:from-[#ea580c] hover:to-[#dc4d00] transition-all duration-200 shadow-sm ${compact ? 'px-3 py-1 text-[11px]' : 'px-4 py-1.5 text-[13px]'}`
                  }
                >
                  <Instagram className={`${compact ? 'w-[11px] h-[11px]' : 'w-[13px] h-[13px]'}`} />
                  {store.secondaryLinkLabel || 'Shop Now'}
                </a>
              )}
              {storeAny.phone && (
                <a
                  href={`tel:${String(storeAny.phone).replace(/[^\d+]/g, '')}`}
                  onClick={(e) => e.stopPropagation()}
                  className={`inline-flex items-center gap-1 font-semibold text-white bg-emerald-600 rounded-full hover:bg-emerald-700 transition-all duration-200 shadow-sm ${compact ? 'px-3 py-1 text-[11px]' : 'px-4 py-1.5 text-[13px]'}`}
                >
                  <Phone className={`${compact ? 'w-[11px] h-[11px]' : 'w-[13px] h-[13px]'}`} />
                  Call
                </a>
              )}
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}

export default function Shopping() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedHeroLocation, setSelectedHeroLocation] = useState('All Locations');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState('');
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
        setDropdownSearch('');
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => dropdownInputRef.current?.focus(), 50);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const filteredHeroLocations = HERO_LOCATIONS.filter((loc) =>
    loc.toLowerCase().includes(dropdownSearch.toLowerCase())
  );

  const mc = useManagedCollection<any>("shopping", SHOPPING_STORES, (s, i) => slugify(s.name || String(i)));

  const effectiveLocation = selectedHeroLocation !== 'All Locations' ? selectedHeroLocation : null;
  const displayLocation = effectiveLocation ?? 'Dallas-DFW';

  const filteredStores = mc.items.filter((store) => {
    const matchesQuery = searchQuery.trim() === '' || store.name.toLowerCase().includes(searchQuery.toLowerCase()) || (store.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLocation = !effectiveLocation || store.location.toLowerCase().includes(effectiveLocation.toLowerCase());
    const matchesCategory = !selectedCategory || (store.categories ? toTypeArray(store.categories).includes(selectedCategory) : (store.category || '') === selectedCategory);
    return matchesQuery && matchesLocation && matchesCategory;
  });

  // Respect the admin-managed order (drag-reorder) instead of alphabetical, so
  // editing a card doesn't move it and reordering persists on the public site.
  const allStores = [...filteredStores].sort((a: any, b: any) => (a._order ?? 0) - (b._order ?? 0));
  const featuredStores = allStores.filter((s) => FEATURED_STORE_NAMES.has(s.name));
  // "More Shopping" is sorted alphabetically by store name per user request.
  const nonFeaturedStores = allStores
    .filter((s) => !FEATURED_STORE_NAMES.has(s.name))
    .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));

  const hasActiveFilters = !!selectedCategory;

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        .filter-scroll::-webkit-scrollbar { display: none; }
        .filter-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Hero */}
      <section className="relative w-full" style={{ minHeight: "160px" }}>
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1781297913510-psg2fe.png"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/45" />
          <svg viewBox="0 0 1440 24" preserveAspectRatio="none" style={{ position: 'absolute', left: 0, bottom: '-1px', width: '100%', height: '18px', display: 'block' }}>
            <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
          </svg>
        </div>
        <div className="relative max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex items-center justify-center" style={{ zIndex: 10, minHeight: "160px" }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="w-full max-w-[740px]">
            <div className="rounded-md" style={{ boxShadow: '0 0 40px 12px rgba(255,255,255,0.45), 0 0 80px 24px rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
              <div className="flex flex-col sm:flex-row bg-white rounded-md shadow-md h-auto sm:h-[40px]">
                <div className="flex-1 px-3 py-2 sm:py-0 flex items-center border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px]">
                  <span className="text-[11px] text-gray-600 font-semibold shrink-0 mr-2 hidden sm:inline">Find a Store</span>
                  <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Type a store name..." className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent" />
                </div>
                <div ref={dropdownRef} className="relative border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px] sm:w-[170px] md:w-[190px] shrink-0" style={{ zIndex: 99999 }}>
                  <button onClick={() => setShowDropdown(!showDropdown)} className="w-full h-full px-3 py-2 sm:py-0 flex items-center gap-1.5 text-left min-h-[40px]">
                    <MapPin className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <span className="text-[12px] text-gray-700 font-medium truncate flex-1">{selectedHeroLocation}</span>
                    <svg className={'w-3 h-3 text-gray-400 shrink-0 transition-transform ' + (showDropdown ? 'rotate-180' : '')} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showDropdown && (
                    <div
                      className="absolute top-full left-0 bg-white border border-gray-100 rounded-lg overflow-hidden"
                      style={{ zIndex: 99999, marginTop: '4px', width: '200px', boxShadow: '0 10px 24px rgba(15,23,42,0.16)', fontFamily: "'Poppins', sans-serif" }}
                    >
                      <div className="px-3 py-2 border-b border-gray-100">
                        <input
                          ref={dropdownInputRef}
                          type="text"
                          value={dropdownSearch}
                          onChange={(e) => setDropdownSearch(e.target.value)}
                          placeholder="Search locations..."
                          className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent"
                        />
                      </div>
                      <div className="overflow-y-auto" style={{ maxHeight: '200px' }}>
                        {filteredHeroLocations.length === 0 ? (
                          <div className="px-3 py-4 text-center text-[12px] text-gray-400">No locations found</div>
                        ) : (
                          filteredHeroLocations.map((loc) => (
                            <button
                              key={loc}
                              onClick={() => { setSelectedHeroLocation(loc); setShowDropdown(false); setDropdownSearch(''); }}
                              className="w-full text-left flex items-center transition-colors duration-150"
                              style={{
                                padding: '8px 12px',
                                fontSize: '13px',
                                fontWeight: selectedHeroLocation === loc ? 700 : 500,
                                color: selectedHeroLocation === loc ? '#f97316' : '#111827',
                                background: selectedHeroLocation === loc ? '#fff3e8' : 'transparent',
                                height: '36px',
                              }}
                              onMouseEnter={e => { if (selectedHeroLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = '#fff7ed'; }}
                              onMouseLeave={e => { if (selectedHeroLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                            >
                              {loc}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <button onClick={() => setShowDropdown(false)} className="bg-[#f97316] hover:bg-[#ea580c] text-white text-[13px] font-bold px-5 py-2.5 sm:py-0 transition-colors duration-200 shrink-0 min-h-[40px] sm:rounded-r-md w-full sm:w-auto">
                  <span className="flex items-center justify-center gap-1.5">
                    <Search className="w-3.5 h-3.5 sm:hidden" />
                    Search
                  </span>
                </button>
              </div>
            </div>


          </motion.div>
        </div>
      </section>
      <NewsTicker />

      {/* Secondary Category Nav */}
      <CategoryNav />

      {/* ── Filter Bar ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1320px] mx-auto px-4 pt-6 pb-4 space-y-4">

          {/* Explore by Type */}
          <div>
            <p className="text-[11px] font-bold text-gray-400 uppercase mb-4" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '0.12em' }}>
              Explore by Type
            </p>
            <div className="flex gap-6 overflow-x-auto filter-scroll pb-2">
              {[
                { name: 'Clothing', image: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-12/51eb47ca8043.jpeg' },
                { name: 'Jewelry', image: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-12/f940f0a379b9.jpeg' },
                { name: 'Accessories', image: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-24/6ebf1f84ab6d.png' },
                { name: 'Global Brands', image: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-12/5d59bb34ca94.jpeg' }
              ].map((cat) => {
                const isActive = selectedCategory === cat.name;
                return (
                <button
                  key={cat.name}
                  onClick={() => setSelectedCategory(isActive ? null : cat.name)}
                  className="flex flex-col items-center gap-2 shrink-0 group"
                >
                  <div className={`w-[68px] h-[68px] rounded-full p-[2px] transition-transform duration-200 shadow-sm ${isActive ? 'bg-gradient-to-tr from-[#f97316] to-[#ea580c] scale-110' : 'bg-gradient-to-tr from-[#f59e0b] to-[#ea580c] group-hover:scale-105'}`}>
                    <div className="w-full h-full rounded-full border-[2.5px] border-white overflow-hidden bg-white">
                      <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                    </div>
                  </div>
                  <span className={`text-[12px] font-medium transition-colors ${isActive ? 'text-[#f97316] font-semibold' : 'text-gray-700'}`} style={{ fontFamily: "'Poppins', sans-serif" }}>{cat.name}</span>
                </button>
                );
              })}

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={() => { setSelectedCategory(null); }}
                  className="flex items-center gap-1.5 rounded-full border border-dashed whitespace-nowrap transition-colors duration-150 shrink-0"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    height: '35px',
                    padding: '0 13px',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    color: '#6b7280',
                    background: '#ffffff',
                    borderColor: '#d1d5db',
                    boxShadow: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#f97316';
                    e.currentTarget.style.borderColor = '#f97316';
                    e.currentTarget.style.background = '#fff7ed';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#6b7280';
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.background = '#ffffff';
                  }}
                >
                  ✕ Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

            {/* ── Shopping Listings ── */}
      <section className="bg-gray-50/50 py-8">
        <div className="max-w-[1320px] mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <h1 className="text-[18px] sm:text-[22px] md:text-[26px] font-bold text-gray-900 mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Top Indian, Pakistani, South Asian &amp; Global Shopping in Dallas–Fort Worth
            </h1>

            {(() => {
              const introParagraphs = [
                "Discover the top Indian, Pakistani, South Asian, and global shopping across Dallas–Fort Worth. Find Indian and Pakistani clothing boutiques, ethnic wear stores, jewelry shops, traditional fashion, accessories, sarees, lehengas, salwar suits, bridal wear, men's ethnic wear, international brands, and more.",
                "Explore shopping destinations in Dallas, Frisco, Plano, Irving, McKinney, Carrollton, Richardson, Allen, The Colony, Prosper, and across the DFW metroplex. Whether you're looking for Indian clothing stores in Dallas, Pakistani boutiques in DFW, Indian jewelry, South Asian fashion, ethnic wear, bridal outfits, Desi accessories, international brands, or popular global fashion and lifestyle brands, discover local shops, boutiques, and retail destinations for all your traditional, contemporary, and everyday shopping needs.",
              ];
              const [firstParagraph, ...restParagraphs] = introParagraphs;

              return (
                <>
                  {/* Mobile: first paragraph + Read more toggle */}
                  <div className="sm:hidden">
                    <p className="text-[12px] text-gray-500 mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      {firstParagraph}
                    </p>
                    {restParagraphs.length > 0 && (
                      <>
                        {isDescExpanded && restParagraphs.map((para, i) => (
                          <p key={i} className="text-[12px] text-gray-500 mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                            {para}
                          </p>
                        ))}
                        <button
                          type="button"
                          onClick={() => setIsDescExpanded((v) => !v)}
                          className="text-[12px] font-semibold mb-4"
                          style={{ fontFamily: "'Poppins', sans-serif", color: '#f97316' }}
                        >
                          {isDescExpanded ? 'Read less' : 'Read more'}
                        </button>
                      </>
                    )}
                    {restParagraphs.length === 0 && <div className="mb-4" />}
                  </div>

                  {/* Desktop / tablet: full text, no truncation */}
                  <div className="hidden sm:block">
                    {introParagraphs.map((para, i) => (
                      <p
                        key={i}
                        className={`text-[13px] text-gray-500 ${i === introParagraphs.length - 1 ? 'mb-6' : 'mb-2'}`}
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        {para}
                      </p>
                    ))}
                  </div>
                </>
              );
            })()}

            <AdminAddBar mc={mc} fields={SHOPPING_FIELDS} label="Add Store" />

            {/* Featured by TastebudsByAnubhi */}
            {featuredStores.length > 0 && (
              <div className="mb-8">
                <h3 className="text-[14px] sm:text-[16px] font-bold text-green-700 mb-3 flex items-center gap-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Featured by TastebudsByAnubhi
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {featuredStores.map((store, index) => (
                    <AdminItem key={store._key} mc={mc} item={store} fields={SHOPPING_FIELDS} title="Store">
                      <ShoppingCard store={store} index={index} compact tightFeatured linkDetail />
                    </AdminItem>
                  ))}
                </div>
              </div>
            )}

            {/* More Shopping */}
            {nonFeaturedStores.length > 0 && (
              <div>
                {featuredStores.length > 0 && (
                  <h3 className="text-[14px] sm:text-[16px] font-bold text-gray-700 mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    More Shopping
                  </h3>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {nonFeaturedStores.map((store, index) => (
                    <AdminItem key={store._key} mc={mc} item={store} fields={SHOPPING_FIELDS} title="Store">
                      <ShoppingCard store={store} index={index} compact linkDetail />
                    </AdminItem>
                  ))}
                </div>
              </div>
            )}

            {allStores.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-400 text-[14px]">No stores found for this search.</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      
    </div>
  );
}
